import { useCallback, useMemo, useRef, useState } from "react";

import {
  type Attempt,
  type SessionResult,
  SessionRecorder,
  recordAttempt,
} from "@nav-games/games-core";
import {
  type BoatPart,
  type BoatView,
  BOAT_PARTS,
  partsForView,
} from "@nav-games/sailing";

import { BoatPartsFigure } from "../../ui/BoatPartsFigure";
import { HelpButton, HelpPanel } from "../../ui/HelpPanel";

interface BoatPartsProps {
  onNavigate: (to: string) => void;
}

const MODULE = "sailing";
const GAME = "boat-parts";
const CARDS_PER_SESSION = 10;
const CHOICES_PER_CARD = 4;

type Scope = "all" | BoatView;

const SCOPES: readonly { id: Scope; label: string }[] = [
  { id: "all", label: "All" },
  { id: "yacht-profile", label: "Yacht profile" },
  { id: "rigging", label: "Rigging" },
  { id: "powerboat", label: "Powerboat" },
  { id: "below-decks", label: "Below decks" },
];

interface Card {
  part: BoatPart;
  choices: readonly BoatPart[];
}

function shuffled<T>(arr: readonly T[]): T[] {
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const a = out[i]!;
    out[i] = out[j]!;
    out[j] = a;
  }
  return out;
}

function buildCard(scope: Scope): Card {
  const pool =
    scope === "all" ? BOAT_PARTS : partsForView(scope as BoatView);
  const part = pool[Math.floor(Math.random() * pool.length)]!;
  // Distractors come from the same view to keep the question fair
  // (a powerboat student shouldn't be punished for not knowing
  // a halyard).
  const samePool = partsForView(part.view).filter((p) => p.id !== part.id);
  const distractors = shuffled(samePool).slice(0, CHOICES_PER_CARD - 1);
  const choices = shuffled([part, ...distractors]);
  return { part, choices };
}

export function BoatParts({ onNavigate }: BoatPartsProps) {
  const [scope, setScope] = useState<Scope>("all");
  const recorder = useMemo(
    () =>
      new SessionRecorder({
        module: MODULE,
        game: GAME,
        length: CARDS_PER_SESSION,
      }),
    [],
  );

  const [index, setIndex] = useState(0);
  const [card, setCard] = useState<Card>(() => buildCard("all"));
  const [shownAt, setShownAt] = useState(() => Date.now());
  const [chosen, setChosen] = useState<string | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const sessionStartedAt = useRef(Date.now());

  const advance = useCallback(() => {
    if (index + 1 >= CARDS_PER_SESSION) {
      setResult(recorder.summarise());
      return;
    }
    setIndex((i) => i + 1);
    setCard(buildCard(scope));
    setShownAt(Date.now());
    setChosen(null);
  }, [index, recorder, scope]);

  const onChoose = useCallback(
    (choiceId: string) => {
      if (chosen || result) return;
      const ok = choiceId === card.part.id;
      const attempt: Omit<Attempt, "module"> = {
        char: card.part.id,
        ok,
        ms: Date.now() - shownAt,
        ts: Date.now(),
      };
      recorder.log(attempt);
      recordAttempt({ ...attempt, module: MODULE });
      setChosen(choiceId);
    },
    [card, chosen, recorder, result, shownAt],
  );

  const onScopeChange = (newScope: Scope) => {
    setScope(newScope);
    setIndex(0);
    setCard(buildCard(newScope));
    setShownAt(Date.now());
    setChosen(null);
    setResult(null);
    sessionStartedAt.current = Date.now();
  };

  if (result) {
    return (
      <EndCard
        result={result}
        sessionStartMs={sessionStartedAt.current}
        onAgain={() => {
          setIndex(0);
          setCard(buildCard(scope));
          setShownAt(Date.now());
          setChosen(null);
          setResult(null);
          sessionStartedAt.current = Date.now();
        }}
        onBack={() => onNavigate("/play/sailing")}
      />
    );
  }

  const revealed = chosen !== null;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "sailing" : null} onClose={() => setHelpOpen(false)} />

      <p className="small muted">
        Question {index + 1} of {CARDS_PER_SESSION}
      </p>

      <div className="card" style={{ padding: 8, marginBottom: 12 }}>
        <div className="small muted" style={{ marginBottom: 4 }}>
          Scope
        </div>
        <div
          style={{ display: "flex", gap: 4, flexWrap: "wrap" }}
        >
          {SCOPES.map((s) => (
            <button
              key={s.id}
              type="button"
              className={scope === s.id ? "" : "secondary"}
              onClick={() => onScopeChange(s.id)}
              style={{ flex: "1 1 80px", fontSize: 12, padding: "6px 4px" }}
              aria-pressed={scope === s.id}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>What's the highlighted part?</h3>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: 8,
            marginBottom: 12,
            background: "var(--bg-elev)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
          }}
        >
          <BoatPartsFigure
            view={card.part.view}
            highlight={card.part.id}
            showAllLabels={revealed}
            size={320}
          />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {card.choices.map((c) => {
            const isCorrect = c.id === card.part.id;
            const isChosen = c.id === chosen;
            return (
              <button
                key={c.id}
                className="secondary"
                disabled={revealed}
                onClick={() => onChoose(c.id)}
                style={{
                  fontSize: 14,
                  padding: "12px 4px",
                  fontWeight: 600,
                  background:
                    revealed && isCorrect
                      ? "rgba(44, 122, 59, 0.18)"
                      : revealed && isChosen
                        ? "rgba(184, 48, 42, 0.18)"
                        : undefined,
                }}
              >
                {c.name}
              </button>
            );
          })}
        </div>

        {revealed && (
          <div
            role="status"
            aria-live="polite"
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 6,
              background:
                chosen === card.part.id
                  ? "rgba(44, 122, 59, 0.10)"
                  : "rgba(184, 48, 42, 0.10)",
            }}
          >
            <p style={{ margin: 0, fontWeight: 600 }}>
              {chosen === card.part.id
                ? `✓ Correct — ${card.part.name}.`
                : `✗ Not quite — that's the ${card.part.name}.`}
            </p>
            {card.part.riggingKind && (
              <p className="small muted" style={{ margin: "4px 0 0" }}>
                {card.part.riggingKind === "standing"
                  ? "Standing rigging — the structural wires holding the mast up."
                  : "Running rigging — the lines that control the sails."}
              </p>
            )}
            <p className="small" style={{ margin: "8px 0 0" }}>
              <strong>What it is:</strong> {card.part.meaning}
            </p>
            <p className="small" style={{ margin: "6px 0 0" }}>
              <strong>Why you care:</strong> {card.part.use}
            </p>
            <button onClick={advance} style={{ marginTop: 12, width: "100%" }}>
              {index + 1 >= CARDS_PER_SESSION ? "See score →" : "Next →"}
            </button>
          </div>
        )}
      </div>

      <button className="secondary" onClick={() => onNavigate("/play/sailing")}>
        ← Back to Sailing
      </button>
    </>
  );
}

function EndCard({
  result,
  sessionStartMs,
  onAgain,
  onBack,
}: {
  result: SessionResult;
  sessionStartMs: number;
  onAgain: () => void;
  onBack: () => void;
}) {
  const elapsedS = Math.round((Date.now() - sessionStartMs) / 1000);
  return (
    <>
      <h2>Session complete</h2>
      <div className="card">
        <p>
          <strong>{result.correct}</strong> / {result.total} correct
          {result.total > 0 && (
            <> ({Math.round((result.correct / result.total) * 100)}%)</>
          )}{" "}
          in {elapsedS}s.
        </p>
      </div>
      <div className="row" style={{ gap: 8, flexDirection: "column", alignItems: "stretch" }}>
        <button onClick={onAgain}>Play another</button>
        <button className="secondary" onClick={onBack}>
          ← Back to Sailing
        </button>
      </div>
    </>
  );
}
